// =============================================================================
// OVERDUE.JS — When an invoice is actually late.
// =============================================================================
// This used to be "14 days since the status last changed", in three different
// places, with the number written out by hand in each. Two problems with that:
//
//   1. The agency works on net 30. A 20-day-old invoice has ten days left to
//      run, but was shown in red as overdue. When healthy invoices are flagged
//      as urgent, the genuinely late ones stop standing out.
//   2. status_updated_at is not the invoice date — it moves on ANY status
//      change. It is the best proxy available while the deal sits in
//      'Invoice Sent', but that is what it is: a proxy.
//
// Everything that reports on overdue money should import from here so the
// dashboard, the reports tab and the weekly digests cannot drift apart.
// =============================================================================

export const DEFAULT_PAYMENT_TERMS_DAYS = 30

const MS_PER_DAY = 86400000

/** The date an invoice was (as best we can tell) sent. */
export function invoiceSentDate(deal) {
  const raw = deal?.status_updated_at || deal?.created_at || deal?.date
  if (!raw) return null
  const d = new Date(raw)
  return Number.isNaN(d.getTime()) ? null : d
}

/**
 * When payment is due. An explicit invoice_due_date always wins; otherwise the
 * invoice date plus the deal's agreed terms.
 */
export function invoiceDueDate(deal) {
  if (deal?.invoice_due_date) {
    const explicit = new Date(deal.invoice_due_date)
    if (!Number.isNaN(explicit.getTime())) return explicit
  }
  const sent = invoiceSentDate(deal)
  if (!sent) return null
  // Careful: Number(null) is 0 and Number.isFinite(0) is true, so testing the
  // coerced value alone turns "no terms recorded" into "due on receipt" and
  // makes every such invoice overdue on day one. Check for an actual value
  // first. Deals loaded before the payment_terms_days migration have no such
  // field at all, and they must fall back to net 30 like everything else.
  const raw = deal?.payment_terms_days
  const hasTerms = raw !== null && raw !== undefined && raw !== '' && Number.isFinite(Number(raw))
  const terms = hasTerms ? Number(raw) : DEFAULT_PAYMENT_TERMS_DAYS
  return new Date(sent.getTime() + terms * MS_PER_DAY)
}

/**
 * Days past due. Zero or negative means still within terms, so callers can
 * show "due in 4 days" from the same number.
 */
export function daysOverdue(deal, now = new Date()) {
  const due = invoiceDueDate(deal)
  if (!due) return 0
  return Math.floor((now.getTime() - due.getTime()) / MS_PER_DAY)
}

/** True when this deal is awaiting payment and past its due date. */
export function isOverdue(deal, now = new Date()) {
  if (deal?.status !== 'Invoice Sent') return false
  return daysOverdue(deal, now) > 0
}

/** Overdue deals, most overdue first, each carrying its daysOverdue. */
export function overdueInvoices(deals, now = new Date()) {
  return (deals || [])
    .filter(d => isOverdue(d, now))
    .map(d => ({ ...d, daysOverdue: daysOverdue(d, now) }))
    .sort((a, b) => b.daysOverdue - a.daysOverdue)
}
